/**
 * iframe 雙向通訊服務
 * 處理與父頁面的訊息交換
 */

class IframeMessagingService {
  constructor() {
    this.isIframe = this._detectIframe()
    this.messageHandlers = new Map()
    this.subscriptionProducts = []
    this.debugEnabled = true // 開啟除錯模式
    this.messageCounter = 0
    this.messageBuffer = [] // 緩衝在處理器註冊前收到的消息
    this.maxBufferSize = 20 // 最多緩衝 20 條消息 (增加容量)
    this.bufferTimeout = 30000 // 緩衝消息 30 秒後過期
    this._init()
  }

  /**
   * 檢測是否在 iframe 中
   */
  _detectIframe() {
    return !!(window && window !== window.top)
  }

  /**
   * 初始化通訊服務
   */
  _init() {
    this._log('🚀 初始化 iframe 通訊服務', {
      isIframe: this.isIframe,
      origin: window.location.origin,
      href: window.location.href,
      bufferConfig: {
        maxBufferSize: this.maxBufferSize,
        bufferTimeout: this.bufferTimeout
      }
    })

    if (this.isIframe) {
      window.addEventListener('message', this._handleMessage.bind(this))
      this._log('👂 開始監聽父頁面訊息 & 📦 訊息緩存已啟動', {
        note: '從現在開始緩存所有重要訊息直到處理器註冊'
      })

      // 通知父頁面 iframe 基礎服務已準備就緒
      this._postToParent({
        type: 'IFRAME_READY',
        timestamp: Date.now(),
        stage: 'MESSAGING_INITIALIZED'
      })

      this._log('📤 已發送 IFRAME_READY (基礎初始化)', {
        note: '父頁面現在知道可以開始發送基礎消息，但建議等待 PAYMENT_READY'
      })
    }
    else
      this._log('ℹ️ 非 iframe 環境，跳過通訊初始化')

  }

  /**
   * 處理接收到的訊息
   */
  _handleMessage(event) {
    this.messageCounter++
    const messageId = `msg-${this.messageCounter}`
    const startTime = performance.now()

    // 詳細記錄事件基本資訊
    this._log('📨 收到原始事件', {
      messageId,
      eventType: event.type,
      origin: event.origin,
      source: event.source ? 'parent-window' : 'unknown',
      isTrusted: event.isTrusted,
      timeStamp: event.timeStamp,
      userAgent: navigator.userAgent.substring(0, 100) + '...'
    })

    // 安全檢查 - 這裡需要根據實際域名配置
    // if (event.origin !== 'https://trusted-domain.com') {
    //   this._log('⚠️ 訊息來源驗證失敗', {
    //     messageId,
    //     origin: event.origin,
    //     expected: 'https://trusted-domain.com'
    //   })
    //   return
    // }

    // 驗證事件資料結構
    if (!event.data) {
      this._log('⚠️ 事件缺少 data 屬性', {
        messageId,
        event: {
          type: event.type,
          origin: event.origin,
          hasData: !!event.data
        }
      })
      return
    }

    let parsedData = event.data

    // 如果 data 是字串，嘗試解析 JSON
    if (typeof event.data === 'string') {
      try {
        parsedData = JSON.parse(event.data)
        this._log('🔧 解析字串格式訊息', {
          messageId,
          originalLength: event.data.length,
          parsedSuccessfully: true
        })
      }
      catch (parseError) {
        this._log('❌ JSON 解析失敗', {
          messageId,
          originalData: event.data.substring(0, 200),
          parseError: parseError.message
        }, 'error')
        return
      }
    }

    const { type, data } = parsedData || {}

    // 記錄接收到的訊息詳細資訊
    this._log('📥 處理解析後訊息', {
      messageId,
      type: type || '(未知類型)',
      origin: event.origin,
      hasData: !!data,
      dataSize: data ? JSON.stringify(data).length : 0,
      dataKeys: data && typeof data === 'object' ? Object.keys(data) : [],
      dataType: typeof data,
      timestamp: new Date().toISOString(),
      processingTime: `${(performance.now() - startTime).toFixed(2)}ms`
    })

    // 記錄完整的原始資料（僅在除錯模式）
    if (this.debugEnabled) {
      this._log('🔍 完整資料內容', {
        messageId,
        rawEventData: event.data,
        parsedData: parsedData,
        dataContent: data
      })
    }

    if (!type) {
      this._log('⚠️ 訊息格式錯誤：缺少 type 欄位', {
        messageId,
        eventData: parsedData,
        availableFields: parsedData ? Object.keys(parsedData) : []
      })
      return
    }

    // 記錄處理器查找過程
    const handler = this.messageHandlers.get(type)
    const handlerSearchTime = performance.now()

    this._log('🔍 查找訊息處理器', {
      messageId,
      type,
      handlerFound: !!handler,
      handlerName: handler?.name || '匿名函數',
      totalRegisteredHandlers: this.messageHandlers.size,
      availableHandlers: Array.from(this.messageHandlers.keys()),
      searchTime: `${(handlerSearchTime - startTime).toFixed(2)}ms`
    })

    if (handler) {
      this._log('🎯 執行訊息處理器', {
        messageId,
        type,
        handlerName: handler.name || '匿名函數',
        handlerType: typeof handler,
        dataToProcess: data ? Object.keys(data) : null
      })

      try {
        const handlerStartTime = performance.now()
        const result = handler(data, event)
        const handlerEndTime = performance.now()

        // 記錄處理結果
        this._log('✅ 訊息處理完成', {
          messageId,
          type,
          executionTime: `${(handlerEndTime - handlerStartTime).toFixed(2)}ms`,
          totalTime: `${(handlerEndTime - startTime).toFixed(2)}ms`,
          hasResult: result !== undefined,
          resultType: typeof result
        })

        // 發送接收確認給父頁面
        this._postToParent({
          type: 'MESSAGE_RECEIVED',
          data: {
            originalMessageId: messageId,
            originalType: type,
            processed: true,
            executionTime: `${(handlerEndTime - handlerStartTime).toFixed(2)}ms`,
            timestamp: Date.now(),
            hasResult: result !== undefined
          }
        })

        // 如果處理器有返回值，記錄它
        if (result !== undefined) {
          this._log('📤 處理器返回結果', {
            messageId,
            type,
            result: result
          })
        }

      }
      catch (error) {
        this._log('❌ 處理訊息時發生錯誤', {
          messageId,
          type,
          handlerName: handler.name || '匿名函數',
          error: {
            message: error.message,
            name: error.name,
            stack: error.stack
          },
          executionTime: `${(performance.now() - startTime).toFixed(2)}ms`,
          inputData: data
        }, 'error')

        // 發送錯誤回應給父頁面
        this._postToParent({
          type: 'MESSAGE_PROCESSING_ERROR',
          data: {
            originalMessageId: messageId,
            originalType: type,
            error: error.message,
            timestamp: Date.now()
          }
        })
      }
    }
    else {
      // 沒有找到處理器，檢查是否需要緩衝
      const shouldBuffer = this._shouldBufferMessage(type)

      if (shouldBuffer) 
        this._bufferMessage(messageId, type, data, event, startTime)
      else {
        this._log('🤷 未找到對應的處理器', {
          messageId,
          type,
          registeredHandlers: Array.from(this.messageHandlers.keys()),
          totalHandlers: this.messageHandlers.size,
          suggestion: this.messageHandlers.size === 0
            ? '尚未註冊任何處理器'
            : `可用的處理器: ${Array.from(this.messageHandlers.keys()).join(', ')}`
        })

        // 發送未處理訊息通知給父頁面
        this._postToParent({
          type: 'UNHANDLED_MESSAGE',
          data: {
            originalMessageId: messageId,
            originalType: type,
            availableHandlers: Array.from(this.messageHandlers.keys()),
            timestamp: Date.now()
          }
        })
      }
    }

    // 記錄整個處理過程的總時間
    const totalProcessingTime = performance.now() - startTime
    this._log('⏱️ 訊息處理總結', {
      messageId,
      type,
      totalProcessingTime: `${totalProcessingTime.toFixed(2)}ms`,
      handlerExecuted: !!handler,
      messageNumber: this.messageCounter,
      timestamp: new Date().toISOString()
    })
  }

  /**
   * 向父頁面發送訊息
   */
  _postToParent(message) {
    const messageId = `out-${Date.now()}`

    if (this.isIframe && window.parent) {
      this._log('📤 發送訊息到父頁面', {
        messageId,
        type: message.type,
        hasData: !!message.data,
        dataKeys: message.data ? Object.keys(message.data) : [],
        targetOrigin: '*',
        timestamp: new Date().toISOString(),
        message: message
      })

      try {
        window.parent.postMessage(message, '*')
        this._log('✅ 訊息發送成功', {
          messageId,
          type: message.type
        })
      }
      catch (error) {
        this._log('❌ 訊息發送失敗', {
          messageId,
          type: message.type,
          error: error.message
        }, 'error')
      }
    }
    else {
      this._log('⚠️ 無法發送訊息', {
        messageId,
        type: message.type,
        isIframe: this.isIframe,
        hasParent: !!window.parent,
        reason: !this.isIframe ? '非 iframe 環境' : '無父頁面'
      })
    }
  }

  /**
   * 通知父頁面支付系統已完全準備就緒
   * @param {string} page - 當前頁面類型 (index, products, purchase-result)
   * @param {Object} capabilities - 頁面功能說明
   */
  notifyPaymentReady(page, capabilities = {}) {
    const readyData = {
      type: 'PAYMENT_READY',
      timestamp: Date.now(),
      page,
      capabilities,
      canReceive: [
        'SUBSCRIPTION_PRODUCT_SELECTED',
        'OPEN_SUBSCRIPTION_OVERLAY',
        'PARENT_DATA_RESPONSE'
      ]
    }

    this._postToParent(readyData)

    this._log('📤 已發送 PAYMENT_READY', {
      page,
      capabilities,
      note: '父頁面現在可以安全發送所有支付相關消息'
    })
  }

  /**
   * 註冊訊息處理器
   */
  onMessage(type, handler) {
    this._log('🔗 註冊訊息處理器', {
      type,
      handlerName: handler.name || '匿名函數',
      totalHandlers: this.messageHandlers.size + 1
    })

    this.messageHandlers.set(type, handler)

    // 檢查緩衝中是否有等待的消息
    this._processBufferedMessages(type, handler)
  }

  /**
   * 移除訊息處理器
   */
  offMessage(type) {
    const removed = this.messageHandlers.delete(type)
    this._log('🔌 移除訊息處理器', {
      type,
      removed,
      totalHandlers: this.messageHandlers.size
    })
  }

  /**
   * 發送訂閱產品資料到父頁面
   */
  sendSubscriptionProducts(products, storeState = {}) {
    this.subscriptionProducts = products
    this._postToParent({
      type: 'SUBSCRIPTION_PRODUCTS_DATA',
      data: {
        products,
        storeState: {
          hideLogo: storeState.hideLogo || false,
          token: !!storeState.token, // 只發送是否有 token，不發送實際 token
          user: storeState.user ? {
            id: storeState.user.id,
            username: storeState.user.username
          } : null
        },
        timestamp: Date.now()
      }
    })
  }

  /**
   * 請求父頁面開啟訂閱產品選擇 overlay
   */
  requestOpenSubscriptionOverlay(products, storeState = {}) {
    this._postToParent({
      type: 'OPEN_SUBSCRIPTION_OVERLAY',
      data: {
        products,
        storeState: {
          hideLogo: storeState.hideLogo || false,
          token: !!storeState.token,
          user: storeState.user ? {
            id: storeState.user.id,
            username: storeState.user.username
          } : null
        },
        timestamp: Date.now()
      }
    })
  }


  /**
   * 發送支付狀態到父頁面 (保持現有功能)
   */
  sendPaymentStatus(status, paymentId, extraData = {}) {
    this._postToParent({
      type: 'PAYMENT_STATUS',
      data: {
        msg: status, // 'DONE' or 'FAIL'
        paymentId,
        timestamp: Date.now(),
        ...extraData
      }
    })
  }

  /**
   * 發送訂閱購買完成狀態
   */
  sendSubscriptionPurchaseComplete(status, subscriptionId, orderData = {}) {
    this._postToParent({
      type: 'SUBSCRIPTION_PURCHASE_COMPLETE',
      data: {
        status, // 'SUCCESS' or 'FAILED'
        subscriptionId,
        orderData,
        timestamp: Date.now()
      }
    })
  }

  /**
   * 請求父頁面資料
   */
  requestParentData(dataType, requestId = null) {
    this._postToParent({
      type: 'REQUEST_PARENT_DATA',
      data: {
        dataType,
        requestId: requestId || Date.now(),
        timestamp: Date.now()
      }
    })
  }

  /**
   * 銷毀服務
   */
  destroy() {
    this._log('🗑️ 銷毀通訊服務', {
      handlerCount: this.messageHandlers.size,
      messageCount: this.messageCounter,
      bufferedMessages: this.messageBuffer.length
    })

    if (this.isIframe)
      window.removeEventListener('message', this._handleMessage.bind(this))

    this.messageHandlers.clear()
    this.messageBuffer = [] // 清空緩衝區
  }

  /**
   * 設置除錯模式
   */
  setDebugMode(enabled) {
    this.debugEnabled = enabled
    this._log(`🔧 除錯模式${enabled ? '開啟' : '關閉'}`)
  }

  /**
   * 內部 log 方法
   */
  _log(message, data = null, level = 'log') {
    if (!this.debugEnabled) return

    const timestamp = new Date().toISOString()
    const sessionId = this._getSessionId()
    const frameInfo = this._getFrameInfo()
    const prefix = `[🖼️ IFRAME-${frameInfo.role}] [Session:${sessionId}] [${frameInfo.origin}]`

    // 格式化輸出，根據數據大小選擇輸出方式
    if (data) {
      const dataSize = this._getDataSize(data)

      if (dataSize > 1000) {
        // 大型數據分組顯示
        console.group(`${prefix} ${message} (${dataSize} chars)`)
        console[level]('數據詳情:', data)
        console.groupEnd()
      }
      else 
        console[level](`${prefix} ${message}`, data)
      

      // 保存到內部日誌歷史
      this._saveToLogHistory(level, message, data, timestamp)
    }
    else {
      console[level](`${prefix} ${message}`)
      this._saveToLogHistory(level, message, null, timestamp)
    }
  }

  /**
   * 獲取會話 ID
   */
  _getSessionId() {
    if (!this._sessionId)
      this._sessionId = Math.random().toString(36).substr(2, 6)

    return this._sessionId
  }

  /**
   * 獲取 frame 信息
   */
  _getFrameInfo() {
    const currentOrigin = window.location.origin
    const currentPath = window.location.pathname
    const isIframe = this.isIframe

    // 計算 frame 深度
    let frameDepth = 0
    let currentWindow = window
    try {
      while (currentWindow !== currentWindow.parent && frameDepth < 10) {
        frameDepth++
        currentWindow = currentWindow.parent
      }
    }
    catch (e) {
      frameDepth = 'unknown'
    }

    // 生成角色標識
    let role = 'MAIN'
    if (isIframe) 
      role = `CHILD-L${frameDepth}`
    

    // 簡化的來源標識
    const originShort = currentOrigin.replace(/^https?:\/\//, '').replace(/:\d+$/, '')
    const pathShort = currentPath.split('/').pop() || 'root'

    return {
      role,
      origin: `${originShort}/${pathShort}`,
      depth: frameDepth,
      isIframe,
      fullOrigin: currentOrigin,
      fullPath: currentPath
    }
  }

  /**
   * 計算數據大小
   */
  _getDataSize(data) {
    try {
      return JSON.stringify(data).length
    }
    catch {
      return String(data).length
    }
  }

  /**
   * 保存到日誌歷史
   */
  _saveToLogHistory(level, message, data, timestamp) {
    if (!this._logHistory) 
      this._logHistory = []
    

    this._logHistory.push({
      level,
      message,
      data,
      timestamp,
      sessionId: this._getSessionId(),
      messageCount: this.messageCounter,
      url: window.location.href
    })

    // 只保留最近 100 條記錄
    if (this._logHistory.length > 100) 
      this._logHistory = this._logHistory.slice(-100)
    
  }

  /**
   * 獲取日誌歷史
   */
  getLogHistory(filterLevel = null) {
    if (!this._logHistory) return []

    if (filterLevel) 
      return this._logHistory.filter(log => log.level === filterLevel)
    

    return [...this._logHistory]
  }

  /**
   * 清除日誌歷史
   */
  clearLogHistory() {
    this._logHistory = []
    this._log('🧹 日誌歷史已清除')
  }

  /**
   * 導出日誌歷史為文件
   */
  exportLogHistory() {
    const logs = this.getLogHistory()
    const exportData = {
      exportTime: new Date().toISOString(),
      sessionId: this._getSessionId(),
      totalLogs: logs.length,
      debugInfo: this.getDebugInfo(),
      logs: logs
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `iframe-messaging-logs-${this._getSessionId()}-${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    this._log('📁 日誌歷史已導出', {
      fileName: a.download,
      totalLogs: logs.length
    })
  }

  /**
   * 判斷是否應該緩衝此類型的消息
   */
  _shouldBufferMessage(type) {
    // 緩衝所有重要的消息類型，從渲染開始就儲存
    const bufferedTypes = [
      'SUBSCRIPTION_PRODUCT_SELECTED',
      'OPEN_SUBSCRIPTION_OVERLAY',
      'PARENT_DATA_RESPONSE',
      'PRODUCT_SELECTION',
      'OVERLAY_REQUEST',
      'USER_ACTION',
      'PAYMENT_FLOW',
      'NAVIGATION_REQUEST'
    ]

    // 如果沒有註冊處理器，也緩衝所有有 type 的消息
    const hasHandlers = this.messageHandlers.size > 0

    return bufferedTypes.includes(type) || !hasHandlers
  }

  /**
   * 緩衝消息
   */
  _bufferMessage(messageId, type, data, event, startTime) {
    // 清理過期的緩衝消息
    this._cleanExpiredBufferedMessages()

    // 檢查緩衝區大小
    if (this.messageBuffer.length >= this.maxBufferSize) {
      this._log('⚠️ 緩衝區已滿，移除最舊的消息', {
        bufferSize: this.messageBuffer.length,
        maxSize: this.maxBufferSize
      })
      this.messageBuffer.shift() // 移除最舊的消息
    }

    const bufferedMessage = {
      messageId,
      type,
      data,
      event: {
        origin: event.origin,
        timeStamp: event.timeStamp,
        isTrusted: event.isTrusted
      },
      startTime,
      bufferTime: Date.now(),
      processed: false
    }

    this.messageBuffer.push(bufferedMessage)

    this._log('📦 消息已緩衝', {
      messageId,
      type,
      bufferSize: this.messageBuffer.length,
      maxAge: `${this.bufferTimeout / 1000}s`,
      dataReceived: !!data
    })

    // 發送緩衝確認給父頁面
    this._postToParent({
      type: 'MESSAGE_BUFFERED',
      data: {
        originalMessageId: messageId,
        originalType: type,
        buffered: true,
        bufferSize: this.messageBuffer.length,
        willProcessWhenReady: true,
        timestamp: Date.now()
      }
    })
  }

  /**
   * 處理緩衝的消息
   */
  _processBufferedMessages(type, handler) {
    const messagesToProcess = this.messageBuffer.filter(
      msg => msg.type === type && !msg.processed
    )

    if (messagesToProcess.length === 0) 
      return
    

    this._log('🔄 處理緩衝的消息', {
      type,
      messageCount: messagesToProcess.length,
      handlerName: handler.name || '匿名函數'
    })

    messagesToProcess.forEach(bufferedMsg => {
      const { messageId, data, event, startTime, bufferTime } = bufferedMsg
      const waitTime = Date.now() - bufferTime

      this._log('📤 處理緩衝消息', {
        messageId,
        type,
        waitTime: `${waitTime}ms`,
        totalAge: `${Date.now() - startTime}ms`
      })

      try {
        const handlerStartTime = performance.now()
        const result = handler(data, event)
        const handlerEndTime = performance.now()

        this._log('✅ 緩衝消息處理完成', {
          messageId,
          type,
          waitTime: `${waitTime}ms`,
          executionTime: `${(handlerEndTime - handlerStartTime).toFixed(2)}ms`,
          hasResult: result !== undefined
        })

        // 標記為已處理
        bufferedMsg.processed = true

      }
      catch (error) {
        this._log('❌ 緩衝消息處理錯誤', {
          messageId,
          type,
          waitTime: `${waitTime}ms`,
          error: error.message
        }, 'error')

        // 仍然標記為已處理，避免重複嘗試
        bufferedMsg.processed = true
      }
    })

    // 清理已處理的消息
    this._cleanProcessedBufferedMessages()
  }

  /**
   * 清理過期的緩衝消息
   */
  _cleanExpiredBufferedMessages() {
    const now = Date.now()
    const originalLength = this.messageBuffer.length

    this.messageBuffer = this.messageBuffer.filter(msg => {
      const age = now - msg.bufferTime
      return age < this.bufferTimeout
    })

    const cleanedCount = originalLength - this.messageBuffer.length
    if (cleanedCount > 0) {
      this._log('🧹 清理過期緩衝消息', {
        cleanedCount,
        remainingCount: this.messageBuffer.length,
        timeout: `${this.bufferTimeout / 1000}s`
      })
    }
  }

  /**
   * 清理已處理的緩衝消息
   */
  _cleanProcessedBufferedMessages() {
    const originalLength = this.messageBuffer.length

    this.messageBuffer = this.messageBuffer.filter(msg => !msg.processed)

    const cleanedCount = originalLength - this.messageBuffer.length
    if (cleanedCount > 0) {
      this._log('🧹 清理已處理緩衝消息', {
        cleanedCount,
        remainingCount: this.messageBuffer.length
      })
    }
  }

  /**
   * 獲取除錯資訊
   */
  getDebugInfo() {
    return {
      isIframe: this.isIframe,
      messageCounter: this.messageCounter,
      registeredHandlers: Array.from(this.messageHandlers.keys()),
      productsCount: this.subscriptionProducts.length,
      debugEnabled: this.debugEnabled,
      messageBuffer: {
        size: this.messageBuffer.length,
        maxSize: this.maxBufferSize,
        timeout: this.bufferTimeout,
        bufferedTypes: this.messageBuffer.map(msg => msg.type),
        processedCount: this.messageBuffer.filter(msg => msg.processed).length
      },
      location: {
        origin: window.location.origin,
        href: window.location.href,
        pathname: window.location.pathname
      },
      parent: {
        exists: !!window.parent,
        isParent: window.parent === window,
        origin: window.parent?.location?.origin || 'unknown'
      }
    }
  }

  /**
   * 獲取當前是否在 iframe 中
   */
  getIsIframe() {
    return this.isIframe
  }

  /**
   * 獲取快取的訂閱產品資料
   */
  getCachedSubscriptionProducts() {
    return this.subscriptionProducts
  }

  /**
   * 獲取緩衝區狀態
   */
  getBufferStatus() {
    return {
      size: this.messageBuffer.length,
      maxSize: this.maxBufferSize,
      timeout: this.bufferTimeout,
      messages: this.messageBuffer.map(msg => ({
        messageId: msg.messageId,
        type: msg.type,
        bufferTime: msg.bufferTime,
        age: Date.now() - msg.bufferTime,
        processed: msg.processed
      }))
    }
  }

  /**
   * 手動清理緩衝區
   */
  clearBuffer() {
    const clearedCount = this.messageBuffer.length
    this.messageBuffer = []
    this._log('🧹 手動清理緩衝區', {
      clearedCount
    })
    return clearedCount
  }
}

// 創建單例實例
export const iframeMessaging = new IframeMessagingService()

// 匯出類別供需要時使用
export { IframeMessagingService }

// 預定義的訊息類型常數
export const MESSAGE_TYPES = {
  // 從父頁面接收
  SUBSCRIPTION_PRODUCT_SELECTED: 'SUBSCRIPTION_PRODUCT_SELECTED',
  PARENT_DATA_RESPONSE: 'PARENT_DATA_RESPONSE',

  // 發送到父頁面
  IFRAME_READY: 'IFRAME_READY',
  PAYMENT_READY: 'PAYMENT_READY',
  MESSAGE_RECEIVED: 'MESSAGE_RECEIVED',
  MESSAGE_BUFFERED: 'MESSAGE_BUFFERED',
  MESSAGE_PROCESSING_ERROR: 'MESSAGE_PROCESSING_ERROR',
  UNHANDLED_MESSAGE: 'UNHANDLED_MESSAGE',
  SUBSCRIPTION_PRODUCTS_DATA: 'SUBSCRIPTION_PRODUCTS_DATA',
  OPEN_SUBSCRIPTION_OVERLAY: 'OPEN_SUBSCRIPTION_OVERLAY',
  PAYMENT_STATUS: 'PAYMENT_STATUS',
  SUBSCRIPTION_PURCHASE_COMPLETE: 'SUBSCRIPTION_PURCHASE_COMPLETE',
  REQUEST_PARENT_DATA: 'REQUEST_PARENT_DATA'
}

// 確認服務已在應用程式載入時啟動
if (iframeMessaging.isIframe) 
  console.log('✅ [EARLY-INIT] iframe 通訊服務已在應用程式載入時啟動，訊息緩存功能已就緒')
else 
  console.log('ℹ️ [EARLY-INIT] 非 iframe 環境，通訊服務已載入但未啟動')


export default iframeMessaging